import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Slider } from './ui/slider';
import { Search, Filter, X, RefreshCw } from 'lucide-react';

const AdvancedFilters = ({ 
  onFiltersChange, 
  clusterId, 
  initialFilters = {},
  availableNamespaces = [],
  availableNodes = []
}) => {
  const [filters, setFilters] = useState({
    search: '',
    namespace: '',
    node: '',
    status: '',
    resourceType: 'all', // pods, nodes, all
    cpuUsageRange: [0, 100],
    memoryUsageRange: [0, 100],
    costRange: [0, 1000],
    efficiencyRange: [0, 100],
    showOnlyProblematic: false,
    showOnlyOptimizable: false,
    sortBy: 'name',
    sortOrder: 'asc',
    ...initialFilters
  });

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      namespace: '',
      node: '',
      status: '',
      resourceType: 'all',
      cpuUsageRange: [0, 100],
      memoryUsageRange: [0, 100],
      costRange: [0, 1000],
      efficiencyRange: [0, 100],
      showOnlyProblematic: false,
      showOnlyOptimizable: false,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.namespace && filters.namespace !== 'all') count++;
    if (filters.node && filters.node !== 'all') count++;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.resourceType !== 'all') count++;
    if (filters.cpuUsageRange[0] > 0 || filters.cpuUsageRange[1] < 100) count++;
    if (filters.memoryUsageRange[0] > 0 || filters.memoryUsageRange[1] < 100) count++;
    if (filters.costRange[0] > 0 || filters.costRange[1] < 1000) count++;
    if (filters.efficiencyRange[0] > 0 || filters.efficiencyRange[1] < 100) count++;
    if (filters.showOnlyProblematic) count++;
    if (filters.showOnlyOptimizable) count++;
    return count;
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Running', label: 'Running' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Failed', label: 'Failed' },
    { value: 'Succeeded', label: 'Succeeded' },
    { value: 'Ready', label: 'Ready' },
    { value: 'NotReady', label: 'Not Ready' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'cpuUsage', label: 'CPU Usage' },
    { value: 'memoryUsage', label: 'Memory Usage' },
    { value: 'cost', label: 'Cost' },
    { value: 'efficiency', label: 'Efficiency' },
    { value: 'age', label: 'Age' },
    { value: 'restarts', label: 'Restarts' }
  ];

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Advanced Filters
            {getActiveFiltersCount() > 0 && (
              <Badge variant="secondary" className="ml-2">
                {getActiveFiltersCount()} active
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={resetFilters}
              disabled={getActiveFiltersCount() === 0}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Reset
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Basic Filters - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search pods, nodes..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filters.namespace} onValueChange={(value) => handleFilterChange('namespace', value)}>
            <SelectTrigger>
              <SelectValue placeholder="All Namespaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Namespaces</SelectItem>
              {availableNamespaces.map(ns => (
                <SelectItem key={ns} value={ns}>{ns}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.resourceType} onValueChange={(value) => handleFilterChange('resourceType', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Resource Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resources</SelectItem>
              <SelectItem value="pods">Pods Only</SelectItem>
              <SelectItem value="nodes">Nodes Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Advanced Filters - Expandable */}
        {isExpanded && (
          <div className="space-y-6 pt-4 border-t">
            {/* Resource Usage Ranges */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  CPU Usage: {filters.cpuUsageRange[0]}% - {filters.cpuUsageRange[1]}%
                </label>
                <Slider
                  value={filters.cpuUsageRange}
                  onValueChange={(value) => handleFilterChange('cpuUsageRange', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Memory Usage: {filters.memoryUsageRange[0]}% - {filters.memoryUsageRange[1]}%
                </label>
                <Slider
                  value={filters.memoryUsageRange}
                  onValueChange={(value) => handleFilterChange('memoryUsageRange', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Cost Range: ${filters.costRange[0]} - ${filters.costRange[1]}
                </label>
                <Slider
                  value={filters.costRange}
                  onValueChange={(value) => handleFilterChange('costRange', value)}
                  max={1000}
                  step={10}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Efficiency: {filters.efficiencyRange[0]}% - {filters.efficiencyRange[1]}%
                </label>
                <Slider
                  value={filters.efficiencyRange}
                  onValueChange={(value) => handleFilterChange('efficiencyRange', value)}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>

            {/* Additional Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select value={filters.node} onValueChange={(value) => handleFilterChange('node', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Nodes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Nodes</SelectItem>
                  {availableNodes.map(node => (
                    <SelectItem key={node} value={node}>{node}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.sortBy} onValueChange={(value) => handleFilterChange('sortBy', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.sortOrder} onValueChange={(value) => handleFilterChange('sortOrder', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Checkbox Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="problematic"
                  checked={filters.showOnlyProblematic}
                  onCheckedChange={(checked) => handleFilterChange('showOnlyProblematic', checked)}
                />
                <label htmlFor="problematic" className="text-sm font-medium">
                  Show only problematic resources
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="optimizable"
                  checked={filters.showOnlyOptimizable}
                  onCheckedChange={(checked) => handleFilterChange('showOnlyOptimizable', checked)}
                />
                <label htmlFor="optimizable" className="text-sm font-medium">
                  Show only optimizable resources
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <span className="text-sm font-medium text-gray-600">Active filters:</span>
            {filters.search && (
              <Badge variant="outline" className="flex items-center gap-1">
                Search: {filters.search}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleFilterChange('search', '')}
                />
              </Badge>
            )}
            {filters.namespace && filters.namespace !== 'all' && (
              <Badge variant="outline" className="flex items-center gap-1">
                Namespace: {filters.namespace}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleFilterChange('namespace', 'all')}
                />
              </Badge>
            )}
            {filters.status && filters.status !== 'all' && (
              <Badge variant="outline" className="flex items-center gap-1">
                Status: {filters.status}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleFilterChange('status', 'all')}
                />
              </Badge>
            )}
            {filters.showOnlyProblematic && (
              <Badge variant="outline" className="flex items-center gap-1">
                Problematic Only
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleFilterChange('showOnlyProblematic', false)}
                />
              </Badge>
            )}
            {filters.showOnlyOptimizable && (
              <Badge variant="outline" className="flex items-center gap-1">
                Optimizable Only
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleFilterChange('showOnlyOptimizable', false)}
                />
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdvancedFilters;